export const ROADMAP_AREAS = [
  "Runtime and operations",
  "IEC semantic core",
  "Programming languages",
  "Protocol servers and clients",
  "Fleet and deployment",
  "Simulation and debug",
  "Project and source control",
  "Hardware, fieldbus, and microcontrollers",
];

const ROADMAP_AREA_SUMMARIES = {
  "Runtime and operations": "Deterministic execution, task scheduling, controller state, ownership, isolation, and process-image truth.",
  "IEC semantic core": "IEC types, POUs, task semantics, conversions, retained-schema rules, and the standard library profile.",
  "Programming languages": "Structured Text and independently designed graphical languages on the shared typed runtime model.",
  "Protocol servers and clients": "Explicit client, server, scanner, and adapter roles connected through the unified tag and ownership paths.",
  "Fleet and deployment": "Build identity, signed delivery, reconciliation, rollback, compatibility, and offline edge lifecycle.",
  "Simulation and debug": "Online observation, forces, deterministic simulation, traces, safety lint, and auditable diagnostics.",
  "Project and source control": "Portable projects, semantic versions and diffs, dependency provenance, Git, and PLCopen exchange.",
  "Hardware, fieldbus, and microcontrollers": "Linux edge I/O, hardware abstraction, qualified fieldbuses, and separately gated embedded targets.",
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const textValue = (value, fallback = "") => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  return fallback;
};

const slug = (value) =>
  textValue(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const normalizeStatus = (value) => {
  const normalized = slug(value).replace(/-/g, "_");
  if (["available", "implemented", "complete", "released"].includes(normalized)) {
    return "available";
  }
  if (["configuration_only", "config_only"].includes(normalized)) {
    return "configuration_only";
  }
  if (["in_progress", "active", "engineering"].includes(normalized)) {
    return "in_progress";
  }
  if (["lab_gated", "hardware_lab_required", "hardware_lab_gated"].includes(normalized)) {
    return "lab_gated";
  }
  if (["release_gated", "blocked", "gated"].includes(normalized)) {
    return "release_gated";
  }
  if (["planned", "roadmap", "not_implemented", "future"].includes(normalized)) {
    return "planned";
  }
  return normalized || "planned";
};

const normalizeValidation = (value) => {
  const normalized = slug(value).replace(/-/g, "_");
  if (["hardware_lab_needed", "hardware_lab_gated"].includes(normalized)) {
    return "hardware_lab_required";
  }
  return normalized || "roadmap";
};

const inferArea = (item, areaCatalog = new Map()) => {
  const areaID = slug(item?.area_id);
  const id = slug(item?.id || item?.name || item?.title);
  const sourceArea = slug(item?.area || item?.category);
  const roadmapStage = slug(item?.roadmap_stage);
  const combined = `${sourceArea} ${areaID} ${roadmapStage} ${id}`;

  if (areaID === "runtime") return "Runtime and operations";
  if (areaID === "engineering-operations") return "Simulation and debug";
  if (areaID === "deployment-fleet") return "Fleet and deployment";
  if (areaID === "project-interoperability") return "Project and source control";
  if (areaID === "protocol-io") return "Protocol servers and clients";
  if (["hardware-targets", "advanced-fieldbus"].includes(areaID)) {
    return "Hardware, fieldbus, and microcontrollers";
  }
  if (areaID === "ai-engineering") return "AI engineering";
  if (areaID === "programming") {
    if (
      roadmapStage === "graphical-engineering" ||
      /ladder|function-block-diagram|sequential-function|structured-text|instruction-list|programming-language/.test(id)
    ) {
      return "Programming languages";
    }
    return "IEC semantic core";
  }

  if (/ladder|function-block|sequential-function|structured-text|instruction-list|programming-language/.test(combined)) {
    return "Programming languages";
  }
  if (/plcopen|source-control|project|git|semantic-diff|library|dependency-lock|export|import/.test(combined)) {
    return "Project and source control";
  }
  if (/simulation|simulator|debug|watch|trace|force|lint/.test(combined)) {
    return "Simulation and debug";
  }
  if (/fleet|deployment|artifact|rollout|rollback|edge-runtime|signing/.test(combined)) {
    return "Fleet and deployment";
  }
  if (/fieldbus|ethercat|profibus|profinet|gpio|hardware|microcontroller|board|firmware/.test(combined)) {
    return "Hardware, fieldbus, and microcontrollers";
  }
  if (/protocol|modbus|opcua|opc-ua|s7comm|ethernet-ip|mqtt|sparkplug|dnp3/.test(combined)) {
    return "Protocol servers and clients";
  }
  if (/iec|semantic|data-type|type-system|pou|function-block-instance|standard-library/.test(combined)) {
    return "IEC semantic core";
  }
  if (/runtime|operation|scan|task|scheduler|restart|process-image|controller|isolation/.test(combined)) {
    return "Runtime and operations";
  }

  const exact = ROADMAP_AREAS.find((area) => slug(area) === sourceArea);
  const catalogArea = areaCatalog.get(textValue(item?.area_id));
  return exact || catalogArea?.title || textValue(item?.area || item?.category, "Other roadmap work");
};

const normalizeTextList = (value) =>
  toArray(value)
    .map((entry) => {
      if (typeof entry === "string") return entry.trim();
      return textValue(entry?.title || entry?.name || entry?.label || entry?.id);
    })
    .filter(Boolean);

const normalizeDependencies = (value) =>
  toArray(value)
    .map((entry) => {
      if (typeof entry === "string") {
        return { id: entry.trim(), label: entry.trim() };
      }
      const label = textValue(entry?.title || entry?.name || entry?.label || entry?.id);
      if (!label) return null;
      return {
        id: textValue(entry.id, slug(label)),
        label,
        status: entry.status ? normalizeStatus(entry.status) : "",
      };
    })
    .filter(Boolean);

const normalizeReleaseGates = (value, gateCatalog = new Map()) =>
  toArray(value)
    .map((entry) => {
      if (typeof entry === "string") {
        return { id: slug(entry), label: entry, status: "required", required: true };
      }
      const definition = gateCatalog.get(textValue(entry?.id));
      const label = textValue(
        entry?.title || entry?.name || entry?.label || definition?.label || entry?.id
      );
      if (!label) return null;
      return {
        id: textValue(entry.id, slug(label)),
        label,
        status: slug(entry.status || entry.state || "required").replace(/-/g, "_"),
        required: entry.required !== false,
        detail: textValue(entry.detail || entry.summary || definition?.summary),
      };
    })
    .filter(Boolean);

const phaseFrom = (value, fallbackStatus, phaseMeta = null) => {
  if (value && typeof value === "object") {
    const title = textValue(value.title || value.name || value.label || value.id, "Roadmap");
    return {
      id: textValue(value.id, slug(title)),
      title,
      summary: textValue(value.summary || value.description),
      order: Number.isFinite(value.order)
        ? value.order
        : Number.isFinite(value.sequence)
          ? value.sequence
          : Number.MAX_SAFE_INTEGER,
      status: textValue(value.status),
    };
  }
  if (phaseMeta) return phaseFrom(phaseMeta, fallbackStatus);
  if (textValue(value)) {
    return { id: slug(value), title: textValue(value), summary: "", order: Number.MAX_SAFE_INTEGER };
  }
  if (fallbackStatus === "available" || fallbackStatus === "configuration_only") {
    return { id: "current", title: "Current", summary: "", order: 0 };
  }
  if (fallbackStatus === "in_progress") {
    return { id: "in-progress", title: "In progress", summary: "", order: 1 };
  }
  return { id: "planned", title: "Planned", summary: "", order: 2 };
};

const normalizeConfigure = (value, legacyLocation) => {
  if (value && typeof value === "object") {
    const surface = textValue(value.surface);
    const path = textValue(value.location || value.label || value.path || value.href);
    const label = slug(surface) === "roadmap"
      ? path
      : surface && path && !path.toLowerCase().startsWith(surface.toLowerCase())
      ? `${surface} → ${path}`
      : path || surface;
    return { label, mode: textValue(value.mode) };
  }
  return { label: textValue(value || legacyLocation), mode: "" };
};

const normalizeItem = (
  item,
  phaseMeta = null,
  index = 0,
  areaCatalog = new Map(),
  stageCatalog = new Map(),
  gateCatalog = new Map(),
  strictExecutionTruth = false
) => {
  const title = textValue(item?.title || item?.name || item?.label, "Untitled roadmap item");
  const declaredStatus = normalizeStatus(item?.status || item?.availability);
  const declaredExecutable = typeof item?.executable === "boolean"
    ? item.executable
    : !strictExecutionTruth && declaredStatus === "available";
  const executable = strictExecutionTruth
    ? declaredStatus === "available" && declaredExecutable === true
    : declaredExecutable;
  const status = declaredStatus === "available" && !executable
    ? "configuration_only"
    : declaredStatus;
  const configured = normalizeConfigure(item?.configuration || item?.configure, item?.location);
  const catalogStage = stageCatalog.get(textValue(item?.roadmap_stage));
  return {
    id: textValue(item?.id, `${slug(title) || "roadmap-item"}-${index}`),
    title,
    area: inferArea(item, areaCatalog),
    role: textValue(item?.role),
    status,
    validation: normalizeValidation(item?.validation),
    phase: phaseFrom(item?.phase, status, catalogStage || phaseMeta),
    summary: textValue(item?.summary || item?.description),
    executable,
    maturity: textValue(item?.maturity),
    configure: configured.label,
    configureMode: configured.mode,
    dependencies: normalizeDependencies(item?.dependencies || item?.depends_on),
    releaseGates: normalizeReleaseGates(
      item?.release_gates || item?.releaseGates || item?.gates,
      gateCatalog
    ),
    limitations: normalizeTextList(item?.limitations),
  };
};

const phaseItems = (phase) =>
  toArray(phase?.items).length
    ? phase.items
    : toArray(phase?.capabilities).length
      ? phase.capabilities
      : toArray(phase?.features).length
        ? phase.features
        : toArray(phase?.work_items);

const roadmapEntries = (manifest) => {
  if (Array.isArray(manifest?.roadmap)) {
    return manifest.roadmap.map((item, index) => ({ item, phase: null, index }));
  }
  if (Array.isArray(manifest?.roadmap?.phases)) {
    return manifest.roadmap.phases.flatMap((phase, phaseIndex) =>
      phaseItems(phase).map((item, itemIndex) => ({
        item,
        phase: { ...phase, order: Number.isFinite(phase.order) ? phase.order : phaseIndex },
        index: phaseIndex * 1000 + itemIndex,
      }))
    );
  }
  const nested =
    toArray(manifest?.roadmap?.items).length
      ? manifest.roadmap.items
      : toArray(manifest?.roadmap?.capabilities);
  if (nested.length) {
    return nested.map((item, index) => ({ item, phase: null, index }));
  }
  return toArray(manifest?.capabilities).map((item, index) => ({ item, phase: null, index }));
};

export const normalizePlcRoadmap = (manifest) => {
  const strictExecutionTruth = Number(manifest?.version) >= 3;
  const areaCatalog = new Map(
    toArray(manifest?.areas).map((area, index) => {
      const title = textValue(area?.name || area?.title || area?.label || area?.id, "Other roadmap work");
      return [
        textValue(area?.id, slug(title)),
        {
          id: textValue(area?.id, slug(title)),
          title,
          summary: textValue(area?.summary || area?.description),
          order: Number.isFinite(area?.sequence) ? area.sequence : index,
        },
      ];
    })
  );
  const stageCatalog = new Map(
    toArray(manifest?.stages).map((stage, index) => {
      const title = textValue(stage?.name || stage?.title || stage?.label || stage?.id, "Roadmap");
      return [
        textValue(stage?.id, slug(title)),
        {
          ...stage,
          id: textValue(stage?.id, slug(title)),
          title,
          order: Number.isFinite(stage?.sequence) ? stage.sequence : index,
        },
      ];
    })
  );
  const gateCatalog = new Map(
    toArray(manifest?.release_gate_catalog).map((gate) => [
      textValue(gate?.id),
      {
        label: textValue(gate?.label || gate?.name || gate?.title || gate?.id),
        summary: textValue(gate?.summary || gate?.description),
      },
    ])
  );
  const items = roadmapEntries(manifest).map(({ item, phase, index }) =>
    normalizeItem(item, phase, index, areaCatalog, stageCatalog, gateCatalog, strictExecutionTruth)
  );

  const itemCatalog = new Map(items.map((item) => [item.id, item]));
  items.forEach((item) => {
    item.dependencies = item.dependencies.map((dependency) => {
      const target = itemCatalog.get(dependency.id);
      if (!target) return dependency;
      return {
        ...dependency,
        label: dependency.label === dependency.id ? target.title : dependency.label,
        status: dependency.status || target.status,
      };
    });
  });

  const categoryMap = new Map(ROADMAP_AREAS.map((area) => [area, []]));
  items.forEach((item) => {
    if (!categoryMap.has(item.area)) categoryMap.set(item.area, []);
    categoryMap.get(item.area).push(item);
  });

  const categories = Array.from(categoryMap.entries()).map(([title, categoryItems]) => ({
    id: slug(title),
    title,
    summary:
      Array.from(areaCatalog.values()).find((area) => area.title === title)?.summary ||
      ROADMAP_AREA_SUMMARIES[title] ||
      (title === "AI engineering"
        ? areaCatalog.get("ai-engineering")?.summary || "Guardrailed AI assistance for PLC engineering and operations."
        : ""),
    items: categoryItems,
  }));

  const phaseMap = new Map();
  Array.from(stageCatalog.values()).forEach((phase) => {
    phaseMap.set(phase.id, {
      id: phase.id,
      title: phase.title,
      summary: textValue(phase.summary || phase.description),
      order: phase.order,
      status: textValue(phase.status),
      count: 0,
    });
  });
  items.forEach((item) => {
    const existing = phaseMap.get(item.phase.id);
    if (!existing) {
      phaseMap.set(item.phase.id, { ...item.phase, count: 1 });
      return;
    }
    existing.count += 1;
    existing.order = Math.min(existing.order, item.phase.order);
    if (!existing.summary && item.phase.summary) existing.summary = item.phase.summary;
  });

  const phases = Array.from(phaseMap.values()).sort(
    (left, right) => left.order - right.order || left.title.localeCompare(right.title)
  );
  const counts = items.reduce(
    (next, item) => {
      next.total += 1;
      if (item.executable) next.available += 1;
      if (item.status === "planned") next.planned += 1;
      if (item.status === "lab_gated" || item.validation === "hardware_lab_required") {
        next.lab_gated += 1;
      }
      return next;
    },
    { total: 0, available: 0, planned: 0, lab_gated: 0 }
  );

  return { items, categories, phases, counts };
};

export const canConfigureRoadmapItem = (item) => {
  if (!item?.configure) return false;
  const mode = slug(item.configureMode);
  if (["planned", "none", "unavailable", "not-available", "disabled"].includes(mode)) {
    return false;
  }
  return ["available", "configuration_only"].includes(item.status) ||
    ["available", "configuration", "configuration-only"].includes(mode);
};
